import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Trash2, Type, Phone, ExternalLink, Copy, MessageSquare, Check } from 'lucide-react';
import type { WhatsAppTemplateComponent } from '@/api/tenants/tenants.types';

interface WhatsAppTemplateBuilderProps {
    components: WhatsAppTemplateComponent[];
    onChange: (components: WhatsAppTemplateComponent[]) => void;
}

type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
type HeaderFormat = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
type ButtonType = 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL';

interface ButtonComponent {
    type: ButtonType;
    text: string;
    phone_number?: string;
    url?: string;
}

export const WhatsAppTemplateBuilder: React.FC<WhatsAppTemplateBuilderProps> = ({
    components,
    onChange
}) => {
    const [previewData, setPreviewData] = useState<{ [key: string]: string }>({
        '1': 'John Doe',
        '2': 'ORDER123',
        '3': 'Tomorrow'
    });
    const [isAddingButton, setIsAddingButton] = useState(false);

    const addComponent = (type: ComponentType) => {
        // Don't add if component already exists
        if (components.some(c => c.type === type)) return;
        
        let newComponent: WhatsAppTemplateComponent;
        
        switch (type) {
            case 'HEADER':
                newComponent = {
                    type: 'HEADER',
                    format: 'TEXT',
                    text: ''
                };
                break;
            case 'BODY':
                newComponent = {
                    type: 'BODY',
                    text: 'Hello {{1}}, your order {{2}} is ready!'
                };
                break;
            case 'FOOTER':
                newComponent = {
                    type: 'FOOTER',
                    text: 'Thank you for choosing us!'
                };
                break;
            case 'BUTTONS':
                newComponent = {
                    type: 'BUTTONS',
                    buttons: []
                };
                break;
            default:
                return;
        }

        const newComponents = [...components];
        
        // Ensure proper order: HEADER -> BODY -> FOOTER -> BUTTONS
        if (type === 'HEADER') {
            newComponents.unshift(newComponent);
        } else if (type === 'BUTTONS') {
            newComponents.push(newComponent);
        } else {
            // Insert BODY or FOOTER in correct position
            const headerIndex = newComponents.findIndex(c => c.type === 'HEADER');
            const bodyIndex = newComponents.findIndex(c => c.type === 'BODY');
            
            let insertIndex = 0;
            if (type === 'BODY') {
                insertIndex = headerIndex >= 0 ? headerIndex + 1 : 0;
            } else if (type === 'FOOTER') {
                if (bodyIndex >= 0) insertIndex = bodyIndex + 1;
                else if (headerIndex >= 0) insertIndex = headerIndex + 1;
                else insertIndex = 0;
            }
            
            newComponents.splice(insertIndex, 0, newComponent);
        }
        
        onChange(newComponents);
    };

    const removeComponent = (type: ComponentType) => {
        const newComponents = components.filter(c => c.type !== type);
        onChange(newComponents);
    };

    const updateComponent = (type: ComponentType, updates: Partial<WhatsAppTemplateComponent>) => {
        const newComponents = components.map(component => 
            component.type === type ? { ...component, ...updates } : component
        );
        onChange(newComponents);
    };

    const addButton = () => {
        if (isAddingButton) return; // Prevent double-clicks
        
        const component = components.find(c => c.type === 'BUTTONS');
        if (component) {
            setIsAddingButton(true);
            
            const newButton: ButtonComponent = {
                type: 'QUICK_REPLY',
                text: 'Button Text'
            };
            
            const newButtons = [...(component.buttons || []), newButton];
            updateComponent('BUTTONS', { buttons: newButtons });
            
            // Reset the flag after a short delay
            setTimeout(() => setIsAddingButton(false), 200);
        }
    };

    const updateButton = (buttonIndex: number, updates: Partial<ButtonComponent>) => {
        const component = components.find(c => c.type === 'BUTTONS');
        if (component && component.buttons) {
            const newButtons = [...component.buttons];
            newButtons[buttonIndex] = { ...newButtons[buttonIndex], ...updates };
            updateComponent('BUTTONS', { buttons: newButtons });
        }
    };

    const removeButton = (buttonIndex: number) => {
        const component = components.find(c => c.type === 'BUTTONS');
        if (component && component.buttons) {
            const newButtons = component.buttons.filter((_, i) => i !== buttonIndex);
            updateComponent('BUTTONS', { buttons: newButtons });
        }
    };

    const renderPreview = useCallback((text: string) => {
        if (!text) return text;
        
        return text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
            return previewData[num] || match;
        });
    }, [previewData]);

    const getComponentIcon = (type: string) => {
        switch (type) {
            case 'HEADER': return <Type className="w-4 h-4" />;
            case 'BODY': return <MessageSquare className="w-4 h-4" />;
            case 'FOOTER': return <Type className="w-4 h-4" />;
            case 'BUTTONS': return <Copy className="w-4 h-4" />;
            default: return <Type className="w-4 h-4" />;
        }
    };

    const hasComponent = (type: ComponentType) => {
        return components.some(c => c.type === type);
    };

    return (
        <div className="w-full">
            <div className="flex gap-6 min-h-[600px]">
                {/* Left Column - Building Blocks */}
                <div className="flex-1 space-y-6">
                    <div>
                        <Label className="text-sm font-medium mb-3 block">Select message template building blocks</Label>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                type="button"
                                variant={hasComponent('HEADER') ? "default" : "outline"}
                                size="sm"
                                onClick={() => hasComponent('HEADER') ? removeComponent('HEADER') : addComponent('HEADER')}
                                className="h-8"
                            >
                                {hasComponent('HEADER') && <Check className="w-3 h-3 mr-1" />}
                                Header
                            </Button>
                            <Button
                                type="button"
                                variant={hasComponent('BODY') ? "default" : "outline"}
                                size="sm"
                                onClick={() => hasComponent('BODY') ? removeComponent('BODY') : addComponent('BODY')}
                                className="h-8"
                            >
                                {hasComponent('BODY') && <Check className="w-3 h-3 mr-1" />}
                                Body
                            </Button>
                            <Button
                                type="button"
                                variant={hasComponent('FOOTER') ? "default" : "outline"}
                                size="sm"
                                onClick={() => hasComponent('FOOTER') ? removeComponent('FOOTER') : addComponent('FOOTER')}
                                className="h-8"
                            >
                                {hasComponent('FOOTER') && <Check className="w-3 h-3 mr-1" />}
                                Footer
                            </Button>
                            <Button
                                type="button"
                                variant={hasComponent('BUTTONS') ? "default" : "outline"}
                                size="sm"
                                onClick={() => hasComponent('BUTTONS') ? removeComponent('BUTTONS') : addComponent('BUTTONS')}
                                className="h-8"
                            >
                                {hasComponent('BUTTONS') && <Check className="w-3 h-3 mr-1" />}
                                Buttons
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Build your template with different components. Body component is required.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {components.map((component) => (
                            <div 
                                key={`${component.type}-component`}
                                className="border rounded-lg p-4 space-y-3"
                            >
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
                                    {getComponentIcon(component.type)}
                                    {component.type.charAt(0) + component.type.slice(1).toLowerCase()}
                                    {component.type === 'BODY' && <span className="text-red-500">*</span>}
                                </div>

                                {component.type === 'HEADER' && (
                                    <>
                                        <div>
                                            <Label className="text-xs">Format</Label>
                                                                                    <Select
                                            value={component.format || 'TEXT'}
                                            onValueChange={(value: HeaderFormat) => 
                                                updateComponent('HEADER', { format: value })
                                            }
                                        >
                                            <SelectTrigger className="h-8 cursor-pointer hover:bg-accent hover:text-accent-foreground">
                                                <SelectValue />
                                            </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="TEXT" className="cursor-pointer">Text</SelectItem>
                                                    <SelectItem value="IMAGE" className="cursor-pointer">Image</SelectItem>
                                                    <SelectItem value="VIDEO" className="cursor-pointer">Video</SelectItem>
                                                    <SelectItem value="DOCUMENT" className="cursor-pointer">Document</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {component.format === 'TEXT' && (
                                            <div>
                                                <Label className="text-xs">Header Text</Label>
                                                <Input
                                                    value={component.text || ''}
                                                    onChange={(e) => 
                                                        updateComponent('HEADER', { text: e.target.value.slice(0, 60) })
                                                    }
                                                    placeholder="Header text..."
                                                    className="h-8"
                                                    maxLength={60}
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {component.text?.length || 0}/60 characters
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}

                                {(component.type === 'BODY' || component.type === 'FOOTER') && (
                                    <div>
                                        <Label className="text-xs">
                                            {component.type === 'BODY' ? 'Body Text *' : 'Footer Text'}
                                        </Label>
                                        <Textarea
                                            value={component.text || ''}
                                            onChange={(e) => {
                                                const maxLength = component.type === 'BODY' ? 1024 : 60;
                                                updateComponent(component.type as ComponentType, { text: e.target.value.slice(0, maxLength) });
                                            }}
                                            placeholder={
                                                component.type === 'BODY' 
                                                    ? "Hello {{1}}, your order {{2}} is ready!"
                                                    : "Thank you for choosing us!"
                                            }
                                            rows={3}
                                            className="text-sm"
                                            maxLength={component.type === 'BODY' ? 1024 : 60}
                                        />
                                        <div className="flex justify-between items-center mt-1">
                                            <p className="text-xs text-muted-foreground">
                                                Use {`{{1}}, {{2}}, etc. for dynamic placeholders`}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {component.text?.length || 0}/{component.type === 'BODY' ? 1024 : 60} characters
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {component.type === 'BUTTONS' && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-xs">Buttons (max 3)</Label>
                                            {(!component.buttons || component.buttons.length < 3) && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => addButton()}
                                                    disabled={isAddingButton}
                                                    className="h-6 text-xs"
                                                >
                                                    <Plus className="w-3 h-3 mr-1" />
                                                    Add Button
                                                </Button>
                                            )}
                                        </div>
                                        
                                                                                                {component.buttons?.map((button, buttonIndex: number) => {
                                                            const typedButton = button as ButtonComponent;
                                                            return (
                                                            <div
                                                                key={`button-${buttonIndex}`}
                                                            >
                                                                    <Card className="p-3">
                                                    <div className="space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <Select
                                                                value={typedButton.type}
                                                                onValueChange={(value: ButtonType) => 
                                                                    updateButton(buttonIndex, { type: value })
                                                                }
                                                            >
                                                                <SelectTrigger className="h-7 w-32 cursor-pointer hover:bg-accent hover:text-accent-foreground">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="QUICK_REPLY" className="cursor-pointer">Quick Reply</SelectItem>
                                                                    <SelectItem value="PHONE_NUMBER" className="cursor-pointer">Phone</SelectItem>
                                                                    <SelectItem value="URL" className="cursor-pointer">URL</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => removeButton(buttonIndex)}
                                                                className="h-6 w-6 p-0"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                        
                                                        <Input
                                                            value={typedButton.text}
                                                            onChange={(e) => 
                                                                updateButton(buttonIndex, { text: e.target.value.slice(0, 25) })
                                                            }
                                                            placeholder="Button text"
                                                            className="h-7"
                                                            maxLength={25}
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            {typedButton.text?.length || 0}/25 characters
                                                        </p>
                                                        
                                                        {typedButton.type === 'PHONE_NUMBER' && (
                                                            <div>
                                                                <Input
                                                                    value={typedButton.phone_number || ''}
                                                                    onChange={(e) => 
                                                                        updateButton(buttonIndex, { phone_number: e.target.value.slice(0, 20) })
                                                                    }
                                                                    placeholder="+1234567890"
                                                                    className="h-7"
                                                                    maxLength={20}
                                                                />
                                                                <p className="text-xs text-muted-foreground">
                                                                    {typedButton.phone_number?.length || 0}/20 characters
                                                                </p>
                                                            </div>
                                                        )}
                                                        
                                                        {typedButton.type === 'URL' && (
                                                            <div>
                                                                <Input
                                                                    value={typedButton.url || ''}
                                                                    onChange={(e) => 
                                                                        updateButton(buttonIndex, { url: e.target.value.slice(0, 2000) })
                                                                    }
                                                                    placeholder="https://example.com"
                                                                    className="h-7"
                                                                    maxLength={2000}
                                                                />
                                                                <p className="text-xs text-muted-foreground">
                                                                    {typedButton.url?.length || 0}/2000 characters
                                                                </p>
                                                            </div>
                                                        )}
                                                                                                                        </div>
                                                                    </Card>
                                                            </div>
                                                            );
                                                        })}
                                        </div>
                                    )}
                                </div>
                            ))}

                        {components.length === 0 && (
                            <div className="text-center py-12 text-muted-foreground border rounded-lg border-dashed">
                                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm">Start building your template by selecting components above</p>
                                <p className="text-xs mt-1">Body component is required</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Preview and Guidelines */}
                <div className="flex-1 space-y-6">
                    <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-6">
                        {components.some(c => c.text?.includes('{{')) && (
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                                <Label className="text-xs font-medium mb-3 block">Preview Variables</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    {[1, 2, 3, 4, 5].map(num => {
                                        const hasVariable = components.some(c => c.text?.includes(`{{${num}}}`));
                                        if (!hasVariable) return null;
                                        return (
                                            <div key={num}>
                                                <Label className="text-xs">{`{{${num}}}`}</Label>
                                                <Input
                                                    value={previewData[num.toString()] || ''}
                                                    onChange={(e) => setPreviewData(prev => ({
                                                        ...prev,
                                                        [num.toString()]: e.target.value
                                                    }))}
                                                    placeholder={`Value ${num}`}
                                                    className="h-8 text-xs mt-1"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            {/* WhatsApp Chat Interface */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border max-w-sm mx-auto overflow-hidden">
                                {/* WhatsApp Header */}
                                <div className="bg-[#075E54] text-white p-3 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                        <MessageSquare className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-sm">Your Business</p>
                                        <p className="text-xs opacity-90">online</p>
                                    </div>
                                </div>

                                {/* Chat Background with Authentic WhatsApp Pattern */}
                                <div 
                                    className="min-h-[320px] p-3 relative"
                                    style={{
                                        backgroundColor: '#E5DDD5',
                                        backgroundImage: `url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')`,
                                        backgroundSize: 'cover',
                                        backgroundRepeat: 'repeat',
                                        backgroundPosition: 'center',
                                        backgroundAttachment: 'local',
                                        margin: 0,
                                        padding: '12px'
                                    }}
                                >
                                    {/* WhatsApp Message Bubble */}
                                    <div className="flex justify-end mb-4">
                                        <div className="max-w-[280px]">
                                            {/* Template Message Label */}
                                            <div className="text-center mb-2">
                                                <span className="inline-block bg-white/80 text-gray-600 text-xs px-2 py-1 rounded-full">
                                                    Template Message
                                                </span>
                                            </div>
                                            
                                            {/* Message Bubble */}
                                            <div className="bg-[#DCF8C6] rounded-lg p-3 shadow-sm relative">
                                                {/* Message Tail */}
                                                <div className="absolute -right-2 top-3 w-0 h-0 border-l-[8px] border-l-[#DCF8C6] border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                                                
                                                {/* Message Content */}
                                                <div className="space-y-2">
                                                    {components.map((component) => (
                                                        <div key={`preview-${component.type}`}>
                                                            {component.type === 'HEADER' && component.text && (
                                                                <div className="font-semibold text-sm text-gray-900 mb-1">
                                                                    {renderPreview(component.text)}
                                                                </div>
                                                            )}
                                                            
                                                            {component.type === 'BODY' && component.text && (
                                                                <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                                                                    {renderPreview(component.text)}
                                                                </div>
                                                            )}
                                                            
                                                            {component.type === 'FOOTER' && component.text && (
                                                                <div className="text-xs text-gray-600 mt-2 italic">
                                                                    {renderPreview(component.text)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                    
                                                    {/* Time stamp */}
                                                    <div className="flex justify-end items-center gap-1 mt-2">
                                                        <span className="text-xs text-gray-500">
                                                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                        <div className="flex">
                                                            <div className="w-3 h-3 text-gray-500">
                                                                <svg viewBox="0 0 16 15" className="fill-current">
                                                                    <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l3.61 3.667c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.063-.51z"/>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                                                        {/* Buttons outside message bubble */}
                            {components.some(c => c.type === 'BUTTONS' && c.buttons && c.buttons.length > 0) && (
                                <div className="mt-1">
                                    {(() => {
                                        const buttons = components.find(c => c.type === 'BUTTONS')?.buttons || [];
                                        
                                        if (buttons.length === 1) {
                                            // Single button - full width
                                            const typedButton = buttons[0] as ButtonComponent;
                                            return (
                                                <div className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm">
                                                    <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                                                                            {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                        <span className="truncate">{typedButton.text}</span>
                                                    </div>
                                                </div>
                                            );
                                        } else if (buttons.length === 2) {
                                            // Two buttons - vertically stacked
                                            return (
                                                <div className="space-y-1">
                                                    {buttons.map((button, buttonIndex) => {
                                                        const typedButton = button as ButtonComponent;
                                                        return (
                                                            <div
                                                                key={buttonIndex}
                                                                className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                                                            >
                                                                <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                    {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                    <span className="truncate">{typedButton.text}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        } else if (buttons.length === 3) {
                                            // Three buttons - 2+1 layout (like WhatsApp)
                                            return (
                                                <div className="space-y-1">
                                                    {/* First row - 2 buttons side by side */}
                                                    <div className="flex gap-1">
                                                        {buttons.slice(0, 2).map((button, buttonIndex) => {
                                                            const typedButton = button as ButtonComponent;
                                                            return (
                                                                <div
                                                                    key={buttonIndex}
                                                                    className="flex-1 bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm"
                                                                >
                                                                    <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                                                                                            {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                        <span className="truncate">{typedButton.text}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Second row - 1 button full width */}
                                                    <div>
                                                        {(() => {
                                                            const typedButton = buttons[2] as ButtonComponent;
                                                            return (
                                                                <div className="bg-white rounded-md py-2 px-3 border border-gray-300 shadow-sm">
                                                                    <div className="flex items-center justify-center gap-2 text-[#075E54] text-sm font-medium">
                                                                                                                                            {typedButton.type === 'PHONE_NUMBER' && <Phone className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'URL' && <ExternalLink className="w-4 h-4 flex-shrink-0" />}
                                                                    {typedButton.type === 'QUICK_REPLY' && <MessageSquare className="w-4 h-4 flex-shrink-0" />}
                                                                        <span className="truncate">{typedButton.text}</span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                                        </div>
                                    </div>
                                    
                                    {components.length === 0 && (
                                        <div className="flex items-center justify-center h-32 text-gray-500">
                                            <div className="text-center bg-white/60 rounded-lg p-4">
                                                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">Your template preview will appear here</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Guidelines */}
                    <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4">
                        <h4 className="text-sm font-medium mb-3 text-blue-900 dark:text-blue-100">Template Guidelines</h4>
                        <div className="space-y-2 text-xs text-blue-800 dark:text-blue-200">
                            <p>• Body component is required for all templates</p>
                            <p>• Maximum 3 buttons allowed per template</p>
                            <p>• Use {`{{1}}, {{2}}, etc. for dynamic variables`}</p>
                            <p>• Headers support text, images, videos, or documents</p>
                            <p>• Keep content concise and relevant to your audience</p>
                            <p>• Templates must be approved by Meta before use</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};